import { endOfDay, format, startOfDay } from "date-fns";
import { useState } from "react";
import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  AddVitalIcon,
  ArrowRight,
  ForwardIcon,
  ShareIcon,
} from "../../shared/svgIcon";
import {
  getOrganizations,
  getVitals,
  getVitalsAvailableRange,
  shareVitals,
  getVitalsDetails
} from "../../lib/api";
import { cn, dateConvert } from "../../shared/utils";
import { useStore } from "../../store";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent } from "../ui/dialog";

const VitalHistory = () => {
  let navigate = useNavigate();
  const location = useLocation();
  const from = new URLSearchParams(location.search).get("from");

  const selectedAppointment = useStore((state) => state.selectedAppointment);
  const setSelectedVital = useStore((state) => state.setSelectedVitals);
  const selectedPatient = useStore((state) => state.patient);
  const [endDate, setEndDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [shareEnable, setShareEnable] = useState(false);
  const [availableHospitals, setAvailableHospital] = useState([]);
  const [selectedHospitals, setSelectedHospital] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedVitals, setSelectedVitals] = useState([]);
  const [enableMultiShare, setEnableMultiShare] = useState(false);

  const { data: vitalAvailableRangeRes, isFetching } = useQuery({
    queryKey: ["getVitalsAvailableRange"],
    queryFn: () =>
      getVitalsAvailableRange({ patientId: selectedPatient?.id, limit: 5 }),
    // staleTime: 1 * 60 * 1000,
    enabled: !!selectedPatient?.id,
  });
  const vitalAvailableRange = vitalAvailableRangeRes?.data?.result;

  useEffect(() => {
    if (vitalAvailableRange?.start_date && !isFetching) {
      setStartDate(new Date(vitalAvailableRange?.start_date)?.toISOString());
      setEndDate(new Date(vitalAvailableRange?.end_date)?.toISOString());
    }
  }, [vitalAvailableRange]);

  const {
    data: vitalDetailsRes,
    isLoading,
    isSuccess,
    isPending,
  } = useQuery({
    queryKey: ["getVitalsDetails", selectedPatient?.id, startDate, endDate],
    queryFn: () =>
      getVitals({
        patientId: selectedPatient?.id,
        startDate: dateConvert(new Date(startDate)),
        endDate: dateConvert(new Date(endDate)),
      }),
    // staleTime:1*60*1000,
    enabled: !!selectedPatient?.id && !!startDate,
  });
  // console.log(selectedPatient,'selectedPatient',startDate)
  const vitalData = vitalDetailsRes?.data?.result || [];
  useEffect(() => {
    if (from == "share") {
      setShareEnable(true);
    }
  }, [from]);

  const goToVitals = (item) => {
    // sessionStorage.setItem('currentVitals',JSON.stringify(item))
    setSelectedVital(item);
    navigate("/vitalSummary");
  };

  const addVitals = () => {
    navigate("/addVitals");
  };

  const goToHome = (index) => {
    navigate("/dashboard");
  };
  const handleGoBack = () => {
    navigate(-1);
  };

  const handleStartDateChange = (event) => {
    const newStartDate = event.target.value;
    setStartDate(startOfDay(new Date(newStartDate)).toISOString());
  };
  const handleEndDateChange = (event) => {
    const newEndDate = event.target.value;
    setEndDate(endOfDay(new Date(newEndDate)).toISOString());
  };

  const {
    data: selectedHospitalListRes,
    isLoading: isLoadingHospital,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ["selectedHospitalList"],
    queryFn: () => getOrganizations(selectedPatient?.id),
    enabled: !!selectedPatient?.id,
    refetchOnMount: true,
  });

  useEffect(() => {
    const currentHospitals =
      selectedHospitalListRes?.data?.result?.map((e) => ({
        ...e.organization,
        isActive: false,
      })) || [];
    setAvailableHospital([...currentHospitals]);
  }, [dataUpdatedAt, selectedHospitalListRes]);

  const handleHospitalClick = (hospital) => {
    const isSelected = selectedHospitals?.some(
      (selected) => selected.id === hospital.id
    );
    if (isSelected) {
      const isActive = selectedHospitals?.filter(
        (selected) => selected.id === hospital.id
      )[0].isActive;
      if (isActive) return;
    }

    const updatedHospital = isSelected
      ? selectedHospitals?.filter((selected) => selected.id !== hospital.id)
      : [...selectedHospitals, hospital];
    // console.log(updatedHospital, "updatedHospital");
    setSelectedHospital(updatedHospital);
  };

  const handleVitalSharing = async (payload) => {
    const response = await shareVitals(payload);
    return response;
  };

  const shareVitalWithHospital = async () => {
    // console.log(selectedHospitals, "selectedHospitals");
    for (const vital of selectedVitals) {
      for (const hospital of selectedHospitals) {
        const payload = {
          vitals_id: vital?.id,
          organization_id: hospital?.id,
        };
        const response = await handleVitalSharing(payload);
        if (response?.isSuccess) {
          toast("Vital shared successfully with " + hospital.name);
        } else {
          toast("Failed to share vital with " + hospital.name);
          // Handle failure case if necessary
        }
      }
    }
    setIsDialogOpen(false);
    setEnableMultiShare((prev) => !prev);
    setSelectedVitals([]);
  };

  const shareVitalWithAppointment = async (vital) => {
    const payload = {
      vitals_id: vital?.id,
      organization_id:
        selectedAppointment?.organization?.id ||
        selectedAppointment?.organization_id,
    };

    const response = await handleVitalSharing(payload);
    if (response?.isSuccess) {
      toast("Vital shared successfully.");
      navigate(-1);
    }
  };

  const handleShare = async (event, item) => {
    event.stopPropagation();

    if (selectedAppointment) {
      shareVitalWithAppointment(item);
    } else {
      setSelectedVitals((prevDocuments) => [...prevDocuments, item]);
      setIsDialogOpen(true);
    }
  };

  const updateSelectedVitals = (vital) => {
    // setSelectedVitals((prevDocuments) => [...prevDocuments, vital]);
    const isSelected = selectedVitals?.some(
      (selected) => selected.id === vital.id
    );
    if (isSelected) {
      const isActive = selectedVitals?.filter(
        (selected) => selected.id === vital.id
      )[0].isActive;
      if (isActive) return;
    }

    const updatedVitals = isSelected
      ? selectedVitals?.filter((selected) => selected.id !== vital.id)
      : [...selectedVitals, vital];
    // console.log(updatedVitals, "updatedVitals");
    setSelectedVitals(updatedVitals);
  };

  const handleMultiShare = () => {
    setEnableMultiShare((prev) => !prev);
    setSelectedVitals([]);
  };

  return (
    <div className="bg-gray-50 sh-screen">
      <div className="flex flex-col text-black p-4 pt-6 h-16 fixed max-w  z-50 w-full bg-gray-50 ">
        <div>
          <Link className="flex" onClick={handleGoBack}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
              />
            </svg>
            <div className="pl-2 flex-1 mt-0.5">
              <h4 className="font-semibold text-sm">Vital History</h4>
            </div>
          </Link>
        </div>
        <div className="flex items-center justify-between">
          {!shareEnable && (
            <button
              className="h-10 rounded-[12px] border-2 bg-blue-800 py-2 px-4 text-center text-white text-lg leading-none"
              onClick={() => handleMultiShare()}
            >
              {enableMultiShare ? "Cancel" : "Share"}
            </button>
          )}
          <div className=" p-2  pt-4 flex justify-end">
            <div className="text-xs mt-1 relative ">
              <input
                type="date"
                defaultValue={startDate?.split("T")[0]}
                onChange={handleStartDateChange}
                className="bg-primary text-white w-24 p-1 rounded-lg text-center"
              />
            </div>
            <div className="px-2 mt-1">to</div>
            <div className="text-xs mt-1 relative">
              <input
                type="date"
                defaultValue={endDate?.split("T")[0]}
                onChange={handleEndDateChange}
                className="bg-primary text-white w-24 p-1 rounded-lg text-center"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="  pt-28 pb-1 px-4">
        <div
          className=" flex-1 overflow-scroll hideScroll   flex flex-col "
          style={{ height: "calc(100vh - 180px)" }}
        >
          {vitalData.length > 0 && isSuccess && (
            <div className=" gap-3 md:gap-3    sm:justify-normal grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
              {vitalData.map((vital, index) => (
                <div
                  key={index}
                  className="w-full  rounded-xl border-2  bg-white  text-center flex flex-col  px-4  py-4 cursor-pointer  font-semibold text-lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (enableMultiShare) {
                      updateSelectedVitals(vital);
                    }
                  }}
                >
                  <div
                    className={`flex justify-between items-center  w-full relative ${
                      shareEnable && "pr-8"
                    }`}
                  >
                    <p className="flex font-bold text-lg ">
                      {" "}
                      {enableMultiShare && (
                        <input
                          type="checkbox"
                          className="border-gray-300 rounded h-5 w-5 text-blue-500 bg-blue-100 focus:ring-blue-500 mr-4 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateSelectedVitals(vital);
                          }}
                          checked={selectedVitals.some(
                            (selected) => selected.id === vital.id
                          )}
                          readOnly
                        />
                      )}
                      Vitals
                    </p>
                    {vital.date && (
                      <div className="flex pl-4 py-2   text-sm ">
                        {/* <p className="pr-1">
                          {format(new Date(e.date), "MMM")}
                        </p>
                        <p className="">{format(new Date(e.date), "dd")}</p>,
                        <p className="pr-1">
                          {format(new Date(e.date), "yyyy")}
                        </p>
                        <p className="pl-3 ">
                          {format(new Date(e.date), "EEE")}
                        </p> */}
                        <p className="pr-2 ">
                          {format(new Date(vital.date), "dd")}/
                          {format(new Date(vital.date), "MM")}/
                          {format(new Date(vital.date), "yyyy")}
                        </p>
                        <p className="pl-1 pr-1">
                          {format(new Date(vital.date), "h:mm a")}
                        </p>
                      </div>
                    )}
                    {shareEnable && (
                      <div
                        title="share"
                        className="absolute text-primary right-0 cursor-pointer"
                        onClick={(event) => handleShare(event, vital)}
                      >
                        <ShareIcon />
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 bg-border h-[1px] w-full px-2">
                    {" "}
                  </div>
                  <div
                    className="flex justify-between items-center text-primary  w-full  pt-4 pb-2"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (enableMultiShare) {
                        updateSelectedVitals(vital);
                      }else{
                        goToVitals(vital);
                      }
                    }}
                  >
                    <div className="flex flex-col">
                      <p className="flex font-bold text-base items-start">
                        bmi
                      </p>
                      <div className="flex font-bold text-base items-start">
                        {vital?.data?.bmi?.value ?? "-"}
                      </div>
                    </div>
                    <div className="shrink-0 bg-border h-full w-[1px]"> </div>
                    <div className="flex flex-col">
                      <p className="flex font-bold text-base items-start">
                        Pulse
                      </p>
                      <div className="flex font-bold text-base items-start">
                        {vital?.data?.hr?.value ?? "-"}
                      </div>
                    </div>
                    <div className="shrink-0 bg-border h-full w-[1px]"> </div>
                    <div className="flex flex-col">
                      <p className="flex font-bold text-base items-start">
                        SpO2
                      </p>
                      <div className="flex font-bold text-base items-start">
                        {vital?.data?.spo2?.value ?? "-"}
                      </div>
                    </div>
                    <div className="shrink-0 bg-border h-full w-[1px]"> </div>
                    <div className="flex flex-col">
                      <p className="flex font-semibold text-base items-start">
                        +5
                      </p>
                      <div className="flex font-semibold text-base items-start">
                        more
                      </div>
                    </div>
                    <ArrowRight />
                  </div>
                  {/* <div className="flex justify-end items-center  w-full pt-3 px-4 gap-4">
                    <button
                      className="rounded-[18px] border-2 border-blue-800 py-2 px-4 text-center text-blue-800 text-lg"
                      onClick={() => goToVitals(e)}
                    >
                      View More
                    </button>
                    <button
                      className="rounded-[18px] border-2 border-blue-800 py-2 px-4 text-center text-blue-800 text-lg"
                      onClick={() => handleShare(e)}
                    >
                      Share
                    </button>
                  </div> */}
                </div>
              ))}
            </div>
          )}
          {vitalData.length == 0 && isSuccess && (
            <div className=" flex flex-col  items-center flex-1">
              <div>
                <AddVitalIcon width={"100px"} height={"200px"} />
              </div>
              <h1 className="text-xl font-bold">No Data</h1>
              <p className="text-gray-400 text-base font-semibold m-4">
                Please Add Your Health Information
              </p>
              <button
                className={cn(
                  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
                  "bg-secondary text-primary-foreground shadow hover:opacity-40",
                  "h-10 rounded-md px-8"
                )}
                onClick={goToHome}
              >
                Go Home
              </button>
            </div>
          )}
          {(isLoading || isPending) && (
            <div className=" relative ">
              <div className=" gap-3 md:gap-3 flex-wrap flex-1  sm:justify-normal grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {" "}
                <div className="animate-pulse flex flex-row cursor-pointer p-4 w-full  items-center  bg-gradient-to-r from-white to-zinc-100 rounded-lg border border-neutral-300">
                  <div className=" w-[80px] h-[80px] rounded-full flex-shrink-0  bg-slate-200"></div>
                  <div className="flex flex-col w-full">
                    <div className="m-2 w-full  bg-slate-200 h-4"></div>
                    <div className="m-2  w-full  bg-slate-200 h-4"></div>
                  </div>
                </div>
                <div className="animate-pulse flex flex-row cursor-pointer p-4 w-full  items-center  bg-gradient-to-r from-white to-zinc-100 rounded-lg border border-neutral-300">
                  <div className=" w-[80px] h-[80px] rounded-full flex-shrink-0  bg-slate-200"></div>
                  <div className="flex flex-col w-full">
                    <div className="m-2 w-full  bg-slate-200 h-4"></div>
                    <div className="m-2  w-full  bg-slate-200 h-4"></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="bg-white h-16 fixed flex justify-center bottom-0 w-full">
        {selectedVitals.length == 0 || !enableMultiShare ? (
          <button
            className="w-11/12 h-14 p-3 bg-blue-800 rounded-[18px] text-white font-semibold"
            onClick={addVitals}
          >
            New
          </button>
        ) : (
          <button
            className="w-11/12 h-14 p-3 bg-blue-800 rounded-[18px] text-white font-semibold"
            onClick={(e) => setIsDialogOpen(true)}
          >
            {selectedVitals.length} Share
          </button>
        )}
      </div>
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[60%] max-w-[90%]">
          <div className="grid mt-4  gap-2 relative">
            <div className="flex flex-col justify-end  w-full sm:w-1/3 "></div>
            <div className=" flex-1 h-[calc(100vh-50vh)] overflow-scroll hideScroll  text-gray-500  flex flex-col pt-2  pb-4">
              {availableHospitals.length > 0 && !isLoading && (
                <div className="flex flex-col gap-3 flex-wrap">
                  {availableHospitals.map((hospital, index) => (
                    <div
                      className={`w-full flex flex-1 items-start space-x-3 py-4 px-2 bg-[#F5F5F5] text-[#32365C] rounded-[18px] border shadow cursor-pointer ${
                        selectedHospitals.some(
                          (selected) => selected.id === hospital.id
                        )
                          ? "border-primary"
                          : ""
                      }`}
                      key={index}
                      onClick={(e) => {
                        handleHospitalClick(hospital);
                      }}
                    >
                      <input
                        type="checkbox"
                        className="border-gray-300 rounded h-5 w-5 min-w-[20px] text-blue-500 bg-blue-100 focus:ring-blue-500 "
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent click event from bubbling to parent div
                          handleHospitalClick(hospital);
                        }}
                        checked={selectedHospitals.some(
                          (selected) => selected.id === hospital.id
                        )}
                        readOnly // Prevent checkbox from being editable by user
                      />

                      <div className="flex flex-col">
                        <div className="relative w-full">
                          <img
                            src={hospital?.logo_url}
                            alt={hospital?.name}
                            className="h-12 object-contain"
                          />
                        </div>
                        <h1 className="text-gray-700 font-medium leading-none">
                          {hospital.name}
                        </h1>
                        <p className="text-xs text-gray-500 mt-2 leading-4">
                          <span className="pr-2">
                            {hospital?.address?.street},
                          </span>
                          <span className="pr-2">
                            {hospital?.address?.city},
                          </span>
                          <span className="pr-2">
                            {hospital?.address?.state}
                          </span>
                          <span className="pr-2">
                            {hospital?.address?.postal_code}
                          </span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {availableHospitals.length == 0 && !isLoading && (
                <h1 className="mt-4">No Hospitals found</h1>
              )}
              {isLoading && (
                <div className="flex flex-col gap-3 flex-wrap ">
                  {" "}
                  <div className="animate-pulse flex flex-row cursor-pointer p-4  w-full  items-center  bg-gradient-to-r from-white to-zinc-100 rounded-lg border border-neutral-300">
                    <div className="w-full">
                      <div className="m-2 w-full  bg-slate-200 h-4"></div>
                    </div>
                  </div>
                  <div className="animate-pulse flex flex-row cursor-pointer p-4  w-full  items-center  bg-gradient-to-r from-white to-zinc-100 rounded-lg border border-neutral-300">
                    <div className="w-full">
                      <div className="m-2 w-full  bg-slate-200 h-4"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div
              className="bg-white  w-full flex justify-center"
              onClick={() => shareVitalWithHospital()}
            >
              <button className="  w-11/12  p-3 bg-blue-800 rounded-[18px] text-white font-semibold">
                share
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VitalHistory;
